import { ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function TransformationNarrative() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const stages = [
    {
      title: "Manual",
      description: "Repetitive tasks, human bottlenecks, limited scale",
      status: "Where most businesses start",
    },
    {
      title: "Automated",
      description: "Workflows streamlined, tasks delegated to systems",
      status: "Where most consultants stop",
    },
    {
      title: "Intelligent",
      description: "Systems that think, learn, and improve autonomously",
      status: "Where we take you",
    },
  ];

  return (
    <section id="transformation" className="py-20 md:py-32 px-6 bg-deep-navy relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-bronze rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warm-gold rounded-full blur-3xl" />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 scroll-fade-in ${isVisible ? 'visible' : ''}`}>
          <div className="inline-block px-4 py-1 rounded-full border border-cyan/30 bg-cyan/5 mb-6 text-xs text-cyan font-bold uppercase tracking-wider">
            Our Philosophy
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            From Automation to Transformation
          </h2>
          <p className="text-lg md:text-xl text-light-gray/80 max-w-3xl mx-auto">
            Most companies stop at automation. We go further — embedding intelligence into your workflows so every system becomes a thinking system.
          </p>
        </div>

        {/* Transformation Journey */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-5xl mx-auto">
          {stages.map((stage, index) => (
            <div key={index} className="relative">
              {/* Card */}
              <div
                className={`bg-dark-card rounded-xl border p-8 transition-all duration-300 scroll-scale-in ${
                  index === 2
                    ? "border-cyan/50 shadow-xl shadow-cyan/20 scale-105 md:scale-110"
                    : "border-border/20 hover:border-cyan/30"
                } ${isVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                {/* Stage Number */}
                <div className={`text-sm font-bold mb-2 ${
                  index === 2 ? "text-bronze" : "text-medium-gray"
                }`}>
                  STAGE {index + 1}
                </div>

                {/* Title */}
                <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
                  index === 2 ? "text-cyan" : "text-foreground"
                }`}>
                  {stage.title}
                </h3>

                {/* Description */}
                <p className="text-base text-light-gray/80 mb-6 leading-relaxed">
                  {stage.description}
                </p>

                {/* Status Badge */}
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  index === 2
                    ? "bg-cyan/20 text-cyan"
                    : "bg-muted text-muted-foreground"
                }`}>
                  {stage.status}
                </div>
              </div>

              {/* Arrow between stages (hidden on last item and mobile) */}
              {index < stages.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-20">
                  <ArrowRight className="w-8 h-8 text-cyan" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Message */}
        <div className="text-center mt-16 max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-cyan/10 via-cyan/5 to-cyan/10 rounded-2xl border border-cyan/20 p-8">
            <p className="text-lg text-foreground font-semibold mb-2">
              Transforming Operations, One System at a Time
            </p>
            <p className="text-base text-light-gray/80">
              We don't just automate workflows — we architect intelligent ecosystems that evolve with your business.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
